import { FC, useState, ChangeEvent, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import lRut from 'rut.js'

import CONFIG from '../../../config'
import { IAuthForgotPasswordForm, IAuthForgotPassword, EDocumentType, FormResponseStatus } from '../../models'
import { Button, Input, Loading, Radio, Text } from '../UI'
import { authActions } from '../../store/actions'
import { useUserDocumentsTypes } from '../../hooks'

type ForgotPasswordProps = {
  className?: string,
  onSubmit(status: FormResponseStatus, email: string): void
}

const defaultValues: IAuthForgotPasswordForm = {
  documentTypeId: String(EDocumentType.rut),
  documentNumber: ''
}

const ForgotPassword:FC<ForgotPasswordProps> = ({ className, onSubmit, ...rest })  => {
  const { register, handleSubmit, formState: { errors }, clearErrors, watch, setValue } = useForm<IAuthForgotPasswordForm>({
    defaultValues
  })
  const [submitStoreResp, setSubmitStoreResp] = useState<boolean>(false)
  const [submited, setSubmited] = useState<boolean>(false)
  const dispatch = useDispatch()
  const docType: number = Number(watch('documentTypeId'))
  const documentsTypes = useUserDocumentsTypes()

  const forgetting: any = useSelector((state: any) => state.forgotPassword)

  if(submitStoreResp && forgetting && JSON.stringify(forgetting) !== '{}') {
    if(forgetting.type === 'SUCCESS') {
      setSubmitStoreResp(false)
      setTimeout(() => onSubmit('SUCCESS', forgetting.patient?.patient?.email), 800)

    } else if(forgetting.type === 'ERROR') {
      setSubmitStoreResp(false)
      setTimeout(() => onSubmit('ERROR', forgetting.patient?.patient?.email), 800)
    }
  }

  const onAuthLogin = async (auth: IAuthForgotPasswordForm) => {
    const { documentTypeId, documentNumber } = auth

    const data: IAuthForgotPassword = {
      document_type_id: Number(documentTypeId),
      document_number: (Number(documentTypeId) === EDocumentType.rut ? lRut.clean(documentNumber) : documentNumber)
    }

    dispatch(authActions.forgotPassword(data))
    setSubmitStoreResp(true)
    setSubmited(true)
  }

  const onDocumentTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event
    setValue('documentTypeId', value)
    setValue('documentNumber', '')
    clearErrors()
  }

  useEffect(() => {
      if (docType === EDocumentType.rut) {
        const formattedRut = lRut.format(watch('documentNumber'), { dots: false })
        setValue('documentNumber', formattedRut)
      }
  }, [watch('documentNumber')]);

  return (
    <div className={className} {...rest}>
      <form noValidate autoComplete="off" onSubmit={handleSubmit(onAuthLogin)}>
        <fieldset>
          <Text component="label" size="14" weight="semibold" inline>Tipo de documento:</Text>

          <div className="d-inline-block position-relative align-middle ml-10">
            {documentsTypes.data ?
                documentsTypes.data.map((it: any, i: number) => {
                  const fieldId: string = `${it.name}${i}`
                  return (
                    <div key={i} className={`d-inline-block ${i > 0 ? 'ml-10' : ''}`}>
                      <Radio
                        {...register('documentTypeId', { required: 'Selecciona Tipo de documento' })}
                        id={fieldId}
                        name="documentTypeId"
                        value={it.id}
                        defaultChecked={it.id === defaultValues.documentTypeId}
                        onChange={onDocumentTypeChange}
                      />
                      <Text component="label" htmlFor={fieldId} size="14" weight="semibold">{it.name}</Text>
                    </div>
                  )
                })
              :
                <Loading size="sm" className="align-middle" />
            }
          </div>
          {errors.documentTypeId && <Text component="label" size="12" color="danger" className="d-block mt-5">{errors.documentTypeId?.message}</Text>}
        </fieldset>

        <fieldset className="mt-30">
          {docType === EDocumentType.rut &&
            <>
              <Text component="label" htmlFor="documentNumber" size="14" weight="semibold">{CONFIG.AUTH.LABEL.RUT}</Text>
              <Input
                {...register('documentNumber', {
                  required: `Digita ${CONFIG.AUTH.LABEL.RUT}`,
                  pattern: {
                    value: CONFIG.FORM.PATTERN.RUT.EXP,
                    message: `Digita ${CONFIG.AUTH.LABEL.RUT} válido`
                  },
                  validate: value => lRut.validate(value) || `Digita ${CONFIG.AUTH.LABEL.RUT} válido`
                })}
                id="documentNumber"
                name="documentNumber"
                placeholder={CONFIG.AUTH.PLACEHOLDER.RUT}
                maxLength={CONFIG.FORM.PATTERN.RUT.MAXLENGHT}
                fullWidth
                error={!!errors.documentNumber}
                className="mt-5"
              />
            </>
          }

          {docType === EDocumentType.passport &&
            <>
              <Text component="label" htmlFor="documentNumber" size="14" weight="semibold">{CONFIG.AUTH.LABEL.PASSPORT}</Text>
              <Input
                {...register('documentNumber', {
                  required: `Digita ${CONFIG.AUTH.LABEL.PASSPORT}`,
                  pattern: { value: CONFIG.FORM.PATTERN.PASSPORT.EXP, message: `Digita ${CONFIG.AUTH.LABEL.PASSPORT} válido` },
                  validate: value => true
                })}
                id="documentNumber"
                name="documentNumber"
                placeholder={CONFIG.AUTH.PLACEHOLDER.PASSPORT}
                fullWidth
                error={!!errors.documentNumber}
                className="mt-5"
              />
            </>
          }

          {errors.documentNumber && <Text component="label" size="12" color="danger" className="mt-5">{errors.documentNumber?.message}</Text>}
        </fieldset>

        <Button
          type="submit"
          fullWidth
          className="mt-50"
          disabled={submited}
        >RECUPERAR CONTRASEÑA</Button>
      </form>
    </div>
  )
}

export { ForgotPassword }